import { forwardRef } from "react";
import type { ButtonHTMLAttributes } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type Variant = "default" | "ghost" | "danger" | "primary";
type Size = "sm" | "md" | "lg";

const VARIANT: Record<Variant, string> = {
  default: "text-gray-400 hover:bg-gray-50 hover:text-indigo-600",
  ghost: "text-gray-400 hover:bg-transparent hover:text-indigo-600",
  danger: "text-rose-500 hover:bg-rose-50 hover:text-rose-600",
  primary: "bg-indigo-600 text-white hover:bg-indigo-700",
};

const SIZE: Record<Size, { padding: string; icon: number }> = {
  sm: { padding: "p-1.5", icon: 14 },
  md: { padding: "p-2", icon: 16 },
  lg: { padding: "p-2.5", icon: 18 },
};

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: LucideIcon;
  variant?: Variant;
  size?: Size;
  label: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, variant = "default", size = "md", label, className, type = "button", ...props },
  ref,
) {
  const s = SIZE[size];
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex items-center justify-center rounded-lg transition-colors disabled:opacity-30",
        VARIANT[variant],
        s.padding,
        className,
      )}
      {...props}
    >
      <Icon size={s.icon} />
    </button>
  );
});
